import React from 'react';

/**
 * Quiz wording and formatting shared by every surface that prints a sitting:
 * the employee result screen, the session detail panel, Participation
 * Records and the HR answer-sheet modal. One place, so a score never reads
 * differently on two screens.
 */

// Whole-sitting time: '4m 12s', '1h 02m', '38s'.
export const fmtDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

// Live timer: 'mm:ss' (or 'h:mm:ss' past an hour).
export const fmtClock = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mmss = `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return h > 0 ? `${h}:${mmss}` : mmss;
};

export const fmtPercent = (p) => (p == null ? '—' : `${Math.round(Number(p))}%`);

// Two states only, and the "not met" one is amber, never red — it is a
// result to follow up, not an error.
export const QUIZ_VERDICT = {
  pass: { label: 'Passed', cls: 'bg-emerald-100 text-emerald-800' },
  fail: { label: 'Not passed', cls: 'bg-amber-100 text-amber-800' },
};

export const QuizVerdictBadge = ({ passed }) => {
  if (passed == null) return <span className="text-xs text-slate-400">—</span>;
  const v = passed ? QUIZ_VERDICT.pass : QUIZ_VERDICT.fail;
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${v.cls}`}>{v.label}</span>
  );
};

// What L&D decided about a session's quiz (ld_training_sessions.quiz_mode).
// Everyone on the expected list sits it — there is no audience choice.
export const QUIZ_MODE_LABELS = {
  none: 'No quiz',
  bank: 'From the Quiz Bank',
  trainer: 'Trainer will write the quiz',
};

// Standing → badge: the latest sitting is open (in_progress), went idle and
// closed ungraded (expired — a retake is due), or graded (submitted — read
// the verdict next to it: Not passed means a retake is due).
export const QUIZ_STATUS = {
  submitted: { label: 'Submitted', cls: 'bg-green-100 text-green-800' },
  expired: { label: 'Expired', cls: 'bg-amber-100 text-amber-800' },
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-800' },
  not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600' },
};
export const QUIZ_STATUS_ORDER = { not_started: 0, in_progress: 1, expired: 2, submitted: 3 };

export const QuizStatusBadge = ({ status }) => {
  const s = QUIZ_STATUS[status] || QUIZ_STATUS.not_started;
  return <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>;
};

export const QUESTION_TYPE_LABELS = {
  single: 'Single choice',
  multiple: 'Multiple choice',
};
